import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  Stack,
  TextField,
  MenuItem,
  Grid,
  TablePagination
} from "@mui/material";

import PageHeader from "../../components/common/PageHeader";
import { getExpenses } from "../../api/accountingApi";
import PaymentModal from "../../components/accounting/PaymentModal";

export default function Expenses() {

  // =========================
  // STATE
  // =========================
  const [expenses, setExpenses] = useState([]);

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  // 🔥 FIX: separate create modal later
  const [createOpen, setCreateOpen] = useState(false);

  const [filters, setFilters] = useState({
    status: "",
    date_from: "",
    date_to: ""
  });

  // 🔥 PAGINATION STATE
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // =========================
  // LOAD DATA
  // =========================
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await getExpenses({
        status: filters.status || "",
        date_from: filters.date_from || "",
        date_to: filters.date_to || ""
      });

      setExpenses(Array.isArray(res.data) ? res.data : []);

    } catch (err) {
      console.error("Expenses Error:", err);
    }
  };

  // =========================
  // FILTERS
  // =========================
  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const applyFilters = () => {
    loadData();
  };

  // =========================
  // STATUS COLOR
  // =========================
  const getStatusColor = (status) => {
    switch (status) {
      case "PAID":
        return "success";
      case "PARTIAL":
        return "warning";
      case "DELETED":
        return "default";
      default:
        return "error";
    }
  };

  // =========================
  // TOTALS
  // =========================
  const totals = expenses.reduce(
    (acc, e) => {
      acc.total += e.amount || 0;
      acc.paid += e.paid_amount || 0;
      acc.outstanding += e.outstanding_amount || 0;
      return acc;
    },
    { total: 0, paid: 0, outstanding: 0 }
  );

  // =========================
  // PAGINATION HANDLERS
  // =========================
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // =========================
  // UI
  // =========================
  return (
    <Box className="page">

      {/* HEADER */}
      <PageHeader
        title="Expenses"
        subtitle="Track and manage all expenses"
      />

      {/* FILTERS */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>

          <Grid item xs={3}>
            <TextField
              select
              label="Status"
              name="status"
              fullWidth
              value={filters.status}
              onChange={handleFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="PAID">Paid</MenuItem>
              <MenuItem value="PARTIAL">Partial</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={3}>
            <TextField
              type="date"
              label="From Date"
              name="date_from"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.date_from}
              onChange={handleFilterChange}
            />
          </Grid>

          <Grid item xs={3}>
            <TextField
              type="date"
              label="To Date"
              name="date_to"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.date_to}
              onChange={handleFilterChange}
            />
          </Grid>

          <Grid item xs={3}>
            <Button
              variant="contained"
              fullWidth
              sx={{ height: "100%" }}
              onClick={applyFilters}
            >
              Apply Filters
            </Button>
          </Grid>

        </Grid>
      </Paper>

      {/* TOTALS */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction="row" spacing={5}>
          <Typography variant="h6">Total: ₹ {totals.total}</Typography>
          <Typography variant="h6" color="success.main">
            Paid: ₹ {totals.paid}
          </Typography>
          <Typography variant="h6" color="error">
            Outstanding: ₹ {totals.outstanding}
          </Typography>
        </Stack>
      </Paper>

      {/* ACTION BAR */}
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Expense List</Typography>

        {/* ✅ FIXED */}
        <Button
          variant="contained"
          onClick={() => setCreateOpen(true)}
        >
          Create Expense
        </Button>
      </Stack>

      {/* TABLE */}
      <Paper sx={{ borderRadius: 3 }}>
        <Table>

          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Paid</TableCell>
              <TableCell>Outstanding</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>

            {expenses
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((e) => (
                <TableRow key={e.id} hover>

                  <TableCell>{e.id}</TableCell>
                  <TableCell>₹ {e.amount}</TableCell>
                  <TableCell>₹ {e.paid_amount || 0}</TableCell>
                  <TableCell>₹ {e.outstanding_amount || 0}</TableCell>

                  <TableCell>
                    <Chip
                      label={e.status}
                      color={getStatusColor(e.status)}
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={e.status === "PAID"}
                      onClick={() => {
                        setSelectedExpense(e);
                        setPaymentOpen(true);
                      }}
                    >
                      Pay
                    </Button>
                  </TableCell>

                </TableRow>
              ))}

          </TableBody>

        </Table>

        {/* 🔥 PAGINATION */}
        <TablePagination
          component="div"
          count={expenses.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

      </Paper>

      {/* PAYMENT MODAL */}
      <PaymentModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        onSuccess={loadData}
        type="expense"
        referenceId={selectedExpense?.id}
        defaultAmount={selectedExpense?.outstanding_amount}
      />

    </Box>
  );
}